import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DzModalRef } from '@blk/daisy/modules/modals';
import { DataUserDataService } from '@ddt-web/data-service';
import { WebUserDataConstants } from '@ddt-web/domain';
import { ErrorPopupService, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { fromEvent, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { PdfGenerateStoreConfig } from '../../config/store.config';
import { CropperComponent } from '../cropper/cropper.component';

@UntilDestroy()
@Component({
	selector: 'ac-pdf-logo-upload',
	templateUrl: './pdf-logo-upload.component.html',
	styleUrls: ['./pdf-logo-upload.component.scss']
})
export class PdfLogoUploadComponent implements OnInit {
	// ------------------------
	// Angular Variables
	// ------------------------
	@Input() public reportCover: string;
	@Input() public isLandscape: boolean;
	@Input() public showLogoError: boolean;
	@Output() public onFileUpdate: EventEmitter<string> = new EventEmitter<string>();
	@Output() public onFileToggle: EventEmitter<boolean> = new EventEmitter<boolean>();
	@ViewChild('cropper') public cropper: CropperComponent;

	// ------------------------
	// Local Variables
	// ------------------------
	public logoName: string;
	private newLogoName: string;
	public logoBase64: string;
	public logoBase64Raw: string;
	public logoFile: string;
	public includeLogo: boolean = false;
	public cropperConfig: any = { zoomable: true, viewMode: 0, autoCropArea: 1 };
	public loadingDialog: DzModalRef;
	public showCropperModal: boolean = false;
	public showConfirmModal: boolean = false;
	public errorMessage: string;

	// ------------------------
	// Local Configuration
	// ------------------------
	public config: DropzoneConfigInterface = {
		clickable: true,
		maxFiles: null,
		maxFilesize: 2,
		acceptedFiles: 'image/png,image/jpeg',
		createImageThumbnails: false,
		previewTemplate: '<span></span>',
		dictDefaultMessage: '',
		dictUploadCanceled: '',
		dictInvalidFileType: 'Upload failed. The file type is not JPG or PNG. Please try again with a different file type.',
		dictFileTooBig: 'Upload failed. The file size exceeds the 2MB limit. Please try again with a smaller file.',
		accept: (file, done) => { this.onAccept(file, done); }
	};

	constructor(
		private dataUserDataService: DataUserDataService,
		private storeService: StoreService,
		private errorPopupService: ErrorPopupService,
		public domSanitizer: DomSanitizer,
		private changeDetectorRef: ChangeDetectorRef
	) { }


	// ------------------------
	// Angular Lifecycle Hooks
	// ------------------------

	public ngOnInit(): void {
		this.getLogoDetails$().subscribe(data => {
			if (data) {
				this.logoName = data.logoName;
				this.logoBase64 = data.logoData;
				this.onFileUpdate.emit(this.logoBase64);

				if (this.logoBase64) {
					this.toggleLogo(true);
				}
				if (this.loadingDialog) {
					this.loadingDialog.close();
					this.changeDetectorRef.detectChanges();
				}
			} else {
				this.dataUserDataService.fetchUserDataPreference(
					[PdfGenerateStoreConfig.reportConfig],
					WebUserDataConstants.USER_DATA_PREFS.LOGO,
					['logoName_s', 'logoData_ss'],
					false
				);
			}
		});
	}

	// ------------------------
	// Store Subscriptions
	// ------------------------

	public getLogoDetails$(): Observable<any> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			map((data: any) => (data && data.hasOwnProperty('logoName_s') && data.hasOwnProperty('logoData_ss') ?
				{ logoName: data.logoName_s, logoData: data.logoData_ss } : null)),
			untilDestroyed(this)
		);
	}

	// ------------------------
	// User Events
	// ------------------------

	public toggleLogo(value?: boolean): void {
		this.includeLogo = value ? value : !this.includeLogo;
		this.errorMessage = null;
		this.onFileToggle.emit(!this.includeLogo);
	}

	public onError(error: any): void {
		// Object returned by library has the second array element set to the error message
		this.errorMessage = error[1];
		throw new Error('Error while uploading an image to dropzone - ' + error[1]);
	}

	public onAccept(file: File, done: any): void {
		const reader = new FileReader();
		if (file) {
			reader.readAsDataURL(file);
			fromEvent(reader, 'load')
				.pipe(first(), map((event: ProgressEvent) => (event.target as FileReader).result))
				.subscribe(encodedData => {
					this.errorMessage = null;
					this.newLogoName = file.name;
					this.logoBase64Raw = encodedData as string;
					this.showCropperModal = true;
					this.changeDetectorRef.detectChanges();
					done();
				});
		}
	}

	public onFileDelete(): void {
		const preferenceData = {
			logoName_s: '',
			logoData_ss: ''
		};
		this.dataUserDataService.setUserDataPreference(
			[PdfGenerateStoreConfig.reportConfig],
			WebUserDataConstants.USER_DATA_PREFS.LOGO,
			preferenceData,
			false
		);
		this.showConfirmModal = false;
		this.showLoading();
	}

	public onImageCrop(): void {
		this.logoName = this.newLogoName;
		const preferenceData = {
			logoName_s: this.logoName,
			logoData_ss: this.cropper.cropper.getCroppedCanvas({ width: 320, height: 90 }).toDataURL()
		};
		this.dataUserDataService.setUserDataPreference(
			[PdfGenerateStoreConfig.reportConfig],
			WebUserDataConstants.USER_DATA_PREFS.LOGO,
			preferenceData,
			false
		);
		this.showLoading();
		this.showCropperModal = false;
		this.changeDetectorRef.detectChanges();
	}

	public onZoomIn(): void {
		this.cropper.cropper.zoom(0.1);
	}

	public onZoomOut(): void {
		this.cropper.cropper.zoom(-0.1);
	}

	public onCancel(): void {
		this.showCropperModal = false;
		this.changeDetectorRef.detectChanges();
	}

	public showLoading(): void {
		this.errorMessage = null;
		this.loadingDialog = this.errorPopupService.openResponseMessageDialog(PopupDialogObject.create({ title: 'Loading...', showSpinner: true } as PopupDialogOptions));
	}

	public toggleConfirmModal(bool: boolean): void {
		this.showConfirmModal = bool;
		this.changeDetectorRef.detectChanges();
	}
}
